import { useMemo } from 'react';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { CallIntelligenceOrgWetags } from '@wetags/call-intelligence';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { useCallIntelSubscription, generateTrackingId } from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { RequestUpgradeModal } from '@frontend/self-upgrade';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { Button, useModalControl } from '@frontend/design-system';

type CallIntelTrialHeaderActionProps = {
  locationIds: string[];
};

export const CallIntelTrialHeaderAction = ({ locationIds }: CallIntelTrialHeaderActionProps) => {
  const { t } = useTranslation('call-intelligence');
  const { navigate } = useSettingsNavigate();

  const [isWetagEnabled, isWetagLoading] = CallIntelligenceOrgWetags.useWetag('call-intel-trial-in-app-nudges');

  const { trialActiveLocationIds, isLoading } = useCallIntelSubscription({
    locationIds,
  });

  const {
    modalProps: requestUpgradeModalProps,
    openModal: openRequestUpgradeModal,
    closeModal: closeRequestUpgradeModal,
  } = useModalControl();

  const hasUpgradeAccess = useMemo(
    () => trialActiveLocationIds.some((locationId) => hasSchemaACL(locationId, Permission.MANAGE_ADD_ON_SUBSCRIPTIONS)),
    [trialActiveLocationIds]
  );

  const handleSubscribeClick = () => {
    navigate({
      to: '/subscriptions/feature/:id',
      params: { id: 'call-intelligence' },
    });
  };

  if (isWetagLoading || !isWetagEnabled || isLoading || trialActiveLocationIds.length === 0) {
    return null;
  }

  return (
    <>
      {hasUpgradeAccess ? (
        <Button
          variant='primary'
          iconName='award-small'
          onClick={handleSubscribeClick}
          trackingId={generateTrackingId({
            component: 'call-intel-trial-header-action',
            context: 'subscribe-button-click',
          })}
        >
          {t('Subscribe')}
        </Button>
      ) : (
        <Button
          variant='primary'
          iconName='award-small'
          onClick={openRequestUpgradeModal}
          trackingId={generateTrackingId({
            component: 'call-intel-trial-header-action',
            context: 'request-upgrade-button-click',
          })}
        >
          {t('Request Upgrade')}
        </Button>
      )}

      <RequestUpgradeModal
        feature={Feature.CALL_INTELLIGENCE}
        selectedLocationIds={trialActiveLocationIds}
        title={t('Request Call Intelligence')}
        onActionSuccess={closeRequestUpgradeModal}
        {...requestUpgradeModalProps}
      />
    </>
  );
};
